import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getActionableRequests,
  liveAddQueueToRequests,
  updateStatusRequests,
} from "../../slices/requests";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Filters from "../layout/Filters";
import {
  BellAlertIcon,
  BoltIcon,
  BoltSlashIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon,
  HandRaisedIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { getColour } from "../../utils/colours";
import Badge from "../layout/Badge";
import ActionButtons from "../layout/ActionButtons";
import { useActionableRequests } from "../../contexts/ActionableRequestsProvider";
import Table from "../layout/Table";
import { translateText } from "../../utils/translation";

export default function Feed() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const pageNum =
    searchParams.get("page") === null
      ? 1
      : parseInt(searchParams.get("page") as string);
  const searchCategory = searchParams.get("category");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getActionableRequests({
        pageNum,
        category: searchCategory,
        dateRange: dateRangeTerm,
      }),
    );
  }, [dispatch, pageNum, searchCategory, dateRangeTerm]);

  // Set state
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [category, setCategory] = useState({ value: "" });
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { actionable, options } = useAppSelector((state) => state.requests);
  const language = user === null ? "EN" : user.language;

  const requestCategoryOptions =
    options.requests === undefined
      ? []
      : Object.entries(options.requests).map(([key, val]: [any, any]) => {
        return {
          value: key,
          label: val[language],
        };
      });

  // Get context from providers
  const { isOnline } = useActionableRequests();

  // Functions
  function selectRequest(requestId: string) {
    const updatedSelectedRequests = [...selectedRequests];
    if (!updatedSelectedRequests.includes(requestId)) {
      updatedSelectedRequests.push(requestId);
    } else {
      updatedSelectedRequests.splice(
        updatedSelectedRequests.indexOf(requestId),
        1,
      );
    }
    setSelectedRequests(updatedSelectedRequests);
  }

  function selectAllRequests(requestId: any) {
    const currentRequestIds =
      actionable.data === null
        ? []
        : actionable.data.map((request) => request.id);

    // If all are selected, unselect all
    if (
      selectedRequests.length > 0 &&
      selectedRequests.length === currentRequestIds.length
    ) {
      setSelectedRequests([]);
      return;
    }

    // Otherwise, select all
    const requestIds =
      actionable.data === null
        ? []
        : actionable.data.map((request) => request.id);
    setSelectedRequests(requestIds);
  }

  async function markRequestsAsComplete(e: any, args: any[]) {
    e.preventDefault();
    const [requestIds] = args;
    const res: any = await dispatch(
      updateStatusRequests({ requestIds, status: "Complete" }),
    );
    if (res.payload.status === 200) {
      setSelectedRequests([]);
      dispatch(
        addAlert(
          generateAlertText("update_request_status_complete", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(
          generateAlertText("update_request_status_error", language),
          "danger",
          3000,
        ),
      );
    }
  }

  async function markRequestsAsInProgress(e: any, args: any[]) {
    e.preventDefault();
    const [requestIds] = args;
    const res: any = await dispatch(
      updateStatusRequests({ requestIds, status: "In Progress" }),
    );
    if (res.payload.status === 200) {
      setSelectedRequests([]);
      dispatch(
        addAlert(
          generateAlertText("update_request_status_in_progress", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(
          generateAlertText("update_request_status_error", language),
          "danger",
          3000,
        ),
      );
    }
  }

  async function addQueueToActionableRequests(e: any) {
    e.preventDefault();
    const queue = actionable.queue === null ? [] : actionable.queue;
    dispatch(liveAddQueueToRequests(queue));
  }

  function getNumColumnsInGrid() {
    if (actionableQueue.length > 0 && selectedRequests.length === 0) return 1;
    if (actionableQueue.length > 0 && selectedRequests.length > 0) return 3;
    if (actionableQueue.length === 0 && selectedRequests.length > 0) return 2;
    return 3;
  }

  function getSelectedRequestsText(numRequests: number) {
    if (language === "EN") {
      if (numRequests === 1) return `${numRequests} request selected`;
      return `${numRequests} requests selected`;
    }
    if (language === "FR") {
      if (numRequests === 1) return `${numRequests} demande sélectionnée`;
      return `${numRequests} demandes sélectionnées`;
    }
    if (language === "IT") {
      if (numRequests === 1) return `${numRequests} richiesta selezionata`;
      return `${numRequests} richieste selezionate`;
    }
    if (numRequests === 1) return `${numRequests} request selected`;
    return `${numRequests} requests selected`;
  }

  function getNewRequestsText(numRequests: number) {
    if (language === "EN") {
      if (numRequests === 1) return `Get ${numRequests} new request`;
      return `Get ${numRequests} new requests`;
    }
    if (language === "FR") {
      if (numRequests === 1) return `Obtenez ${numRequests} nouvelle demande`;
      return `Obtenez ${numRequests} nouvelles demandes`;
    }
    if (language === "IT") {
      if (numRequests === 1) return `Ottieni ${numRequests} nuova richiesta`;
      return `Ottieni ${numRequests} nuove richieste`;
    }
    if (numRequests === 1) return `Get ${numRequests} new request`;
    return `Get ${numRequests} new requests`;
  }

  const actionableData = actionable.data === null ? [] : actionable.data;
  const actionableQueue = actionable.queue === null ? [] : actionable.queue;

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("feed", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`inline-flex justify-center rounded-full p-2 text-sm bg-white text-secondary hover:bg-white/90 active:bg-white/90 active:text-black pointer-events-none`}
              >
                {isOnline ? (
                  <BoltIcon
                    className="h-5 w-5 text-success"
                    aria-hidden="true"
                  />
                ) : (
                  <BoltSlashIcon
                    className="h-5 w-5 text-danger"
                    aria-hidden="true"
                  />
                )}
              </Button>
            }
          />
        }
      />

      {/* Loading spinner */}
      {actionable.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "category",
                  name: translateText("category", language),
                  options: requestCategoryOptions,
                },
              ]}
              queryParams={[
                ["dateRange", dateRangeTerm],
                ["category", searchCategory],
              ]}
              handlers={{
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
                category: {
                  state: category,
                  stateName: "value",
                  onChange: setCategory,
                },
              }}
              loading={actionable.loading}
              baseUrl="/feed"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {actionableData.length > 0 ? (
              <>
                {/* Table actions */}
                {(selectedRequests.length > 0 ||
                  actionableQueue.length > 0) && (
                    <div className="mx-1">
                      {selectedRequests.length > 0 && (
                        <p className="text-sm text-granite italic">
                          {getSelectedRequestsText(selectedRequests.length)}
                        </p>
                      )}
                      <div
                        className={`mt-2 mb-4 grid grid-cols-1 md:grid-cols-${getNumColumnsInGrid()} space-y-2 md:space-y-0 gap-x-2`}
                      >
                        {/* Get new requests */}
                        {actionableQueue.length > 0 && (
                          <Button
                            type="button"
                            className={`${getButtonStyle("solid", "danger")}`}
                            onClick={addQueueToActionableRequests}
                          >
                            <BellAlertIcon
                              className="w-5 h-5 mr-2"
                              aria-hidden="true"
                            />
                            {getNewRequestsText(actionableQueue.length)}
                          </Button>
                        )}

                        {/* Mark as complete & In progress */}
                        {selectedRequests.length > 0 && (
                          <>
                            <Button
                              type="button"
                              className={`${getButtonStyle("solid", "success")}`}
                              onClick={markRequestsAsComplete}
                              onClickArgs={[selectedRequests]}
                            >
                              <CheckBadgeIcon
                                className="w-5 h-5 mr-2"
                                aria-hidden="true"
                              />
                              {translateText("complete", language)}
                            </Button>
                            <Button
                              type="button"
                              className={`${getButtonStyle("solid", "warning")}`}
                              onClick={markRequestsAsInProgress}
                              onClickArgs={[selectedRequests]}
                            >
                              <HandRaisedIcon
                                className="w-5 h-5 mr-2"
                                aria-hidden="true"
                              />
                              {translateText("in_progress", language)}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                {/* Requests table */}
                <Table
                  header={
                    <tr>
                      <th className="px-2 py-3">
                        <Checkbox
                          id="select-all"
                          onChange={selectAllRequests}
                          checked={
                            selectedRequests.length > 0 &&
                            selectedRequests.length === actionableData.length
                          }
                        />
                      </th>
                      <th className="px-2 py-3">
                        {translateText("created", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("category", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("request", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("detail", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("quantity", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("room", language)}
                      </th>
                      <th className="px-2 py-3">
                        {translateText("status", language)}
                      </th>
                    </tr>
                  }
                  body={actionableData.map((request) => (
                    <tr
                      key={request.id}
                      className={`${selectedRequests.includes(request.id) && "bg-light/20"
                        } hover:bg-light/20`}
                      onClick={() => selectRequest(request.id)}
                    >
                      <td className="border-r border-light/50 px-2 py-3">
                        <Checkbox
                          id={request.id}
                          onChange={selectRequest}
                          checked={selectedRequests.includes(request.id)}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {request.datetime_created.string}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        <Badge
                          text={request.category[1]}
                          bgColour={getColour(request.category[0])}
                          textColour={`${getColour(request.category[0])}-dark`}
                          ringColour={`${getColour(request.category[0])}-dark`}
                        />
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {request.request}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {request.details}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {request.quantity}
                      </td>
                      <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                        {request.room.name}
                      </td>
                      <td className="px-2 py-3">
                        <Badge
                          text={request.status[1]}
                          bgColour={getColour(request.status[0])}
                          textColour={`${getColour(request.status[0])}-dark`}
                          ringColour={`${getColour(request.status[0])}-dark`}
                        />
                      </td>
                    </tr>
                  ))}
                  paginationHasPrevious={actionable.pagination.has_previous}
                  paginationHasNext={actionable.pagination.has_next}
                  paginationCurrentPage={actionable.pagination.current_page}
                  paginationTotalPages={actionable.pagination.total_pages}
                  paginationTotalObjects={actionable.pagination.total_objects}
                  paginationQueryParams={["category", "dateRange"]}
                />
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_requests", language)}
                subtext={translateText("no_requests_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
